/**
 * Committed imports, as bank rows.
 *
 * The review route ends with a count ("5 questions imported"), and a count is
 * only believable if the rows are there when the author goes looking. This maps
 * an accepted `ExtractedQuestion` onto the `LibraryItem` the hub renders, and
 * `importedLibraryItems` reads every commit still standing, so an undo takes
 * the rows back out without a second code path.
 */

import type { QuestionImportDocs } from "@/hooks/use-question-import"
import type { ImportedQuestionDraft } from "@/lib/question-import-model"
import type {
  LibraryBloomLevel,
  LibraryItem,
  LibraryItemType,
  LibraryLevel,
} from "@/lib/mock/library"
import { libraryAiSpotted } from "@/lib/library-ai-spotted"
import {
  normalizeQuestionType,
  questionTypeAcceptsBlanks,
} from "@/lib/question-type"
import {
  needsAttention,
  type ExtractedQuestion,
} from "@/lib/question-import-model"

/**
 * Keyed options for the library row.
 *
 * Choice types store keyed options; fill-in stores accepted answers in
 * the same list so edit can seed the composer without re-reading the
 * import. The answer positions go into a Set first: scanning the list
 * once per option made this quadratic in the option count.
 */
function toLibraryOptions(draft: ImportedQuestionDraft) {
  const acceptsBlanks = questionTypeAcceptsBlanks(draft.type)
  const answerPositions = new Set(draft.answerIndexes)
  return draft.options.map((text, index) => ({
    text,
    isCorrect: acceptsBlanks ? true : answerPositions.has(index),
  }))
}

/** Rows created by an import carry this prefix so they can be pulled back out. */
export const IMPORTED_ROW_PREFIX = "import-row"

/**
 * Importer wording to the storage enum. Shared with the pool adapter
 * (`question-import-to-pool`) so one extracted question cannot be filed as
 * two different types in the two destinations.
 */
function libraryTypeForImportType(type: string): LibraryItemType | undefined {
  return normalizeQuestionType(type) ?? undefined
}

const LEVEL_BY_DIFFICULTY: Record<string, LibraryLevel> = {
  Easy: "easy",
  Medium: "medium",
  Hard: "hard",
}

const BLOOM_LEVELS = new Set<string>([
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
])

function importedRowId(jobId: string, itemId: string): string {
  return `${IMPORTED_ROW_PREFIX}:${jobId}:${itemId}`
}

export function isImportedRowId(id: string): boolean {
  return id.startsWith(`${IMPORTED_ROW_PREFIX}:`)
}

/** Parses `import-row:{jobId}:{itemId}` for untake / delete. */
export function parseImportedRowId(
  id: string,
): { jobId: string; itemId: string } | null {
  if (!isImportedRowId(id)) return null
  const rest = id.slice(IMPORTED_ROW_PREFIX.length + 1)
  const splitAt = rest.indexOf(":")
  if (splitAt <= 0) return null
  return {
    jobId: rest.slice(0, splitAt),
    itemId: rest.slice(splitAt + 1),
  }
}

function toLibraryItem(
  item: ExtractedQuestion,
  context: {
    jobId: string
    sequence: number
    folderId: string | null
    asDraft: boolean
    committedAt: number
    sourceLabel: string
  },
): LibraryItem {
  const committed = new Date(context.committedAt)
  const stamp = committed.toISOString().slice(0, 10)

  return {
    id: importedRowId(context.jobId, item.id),
    questionId: `IMP-${committed.getFullYear()}-${String(context.sequence).padStart(3, "0")}`,
    // No title: an import carries a stem, not the short name a colleague files
    // it under. The row falls back to the question id until somebody names it.
    stem: item.draft.stem,
    topic: item.draft.topic || "Uncategorized",
    status: context.asDraft ? "Draft" : "Saved",
    type: libraryTypeForImportType(item.draft.type) ?? "short_answer",
    difficulty: LEVEL_BY_DIFFICULTY[item.draft.difficulty] ?? "medium",
    author: "You",
    updatedAt: committed.toISOString(),
    createdAt: stamp,
    folderId: context.folderId ?? "root",
    // Choice types store keyed options; fill-in stores accepted answers in the
    // same list so edit can seed the composer without re-reading the import.
    options: item.draft.options.length
      ? toLibraryOptions(item.draft)
      : undefined,
    bloomLevel: BLOOM_LEVELS.has(item.draft.bloom)
      ? (item.draft.bloom as LibraryBloomLevel)
      : undefined,
    usageCount: 0,
    version: "v1",
    importedAt: context.committedAt,
    importSourceLabel: context.sourceLabel,
    importSignals: {
      duplicate:
        item.duplicate !== null && item.duplicateResolution === "unresolved",
      needsAttention: needsAttention(item),
    },
    aiSpotted: libraryAiSpotted({
      reviewResolved: item.reviewResolved === true,
      needsAttention: needsAttention(item),
    }) ?? undefined,
  }
}

/**
 * Every row that should be in the bank because of an import, newest first.
 *
 * Derived rather than stored, so undoing a commit removes the rows by removing
 * the reason they existed.
 */
export function importedLibraryItems(docs: QuestionImportDocs): LibraryItem[] {
  const rows: LibraryItem[] = []
  let sequence = 1

  const batches = Object.values(docs).sort(
    (a, b) => (b.commit?.at ?? 0) - (a.commit?.at ?? 0)
  )

  for (const record of batches) {
    for (const item of record.items) {
      if (item.importedAt === null) continue
      rows.push(
        toLibraryItem(item, {
          jobId: record.id,
          sequence: sequence++,
          folderId: record.destinationFolderId,
          asDraft: record.asDraft,
          committedAt: item.importedAt,
          sourceLabel: record.name,
        })
      )
    }
  }

  return rows
}
